import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Card, CardContent, Typography, TextField, IconButton, Avatar, Paper, Chip, Collapse, Divider, CircularProgress } from '@mui/material';
import { Send, ExpandMore, ExpandLess, AutoAwesome, EmojiEvents, TrendingUp } from '@mui/icons-material';
import { sendToOpenAI } from '../utils/openaiHelper';

const QUICK_ACTIONS = [
  { label: 'What are my tasks?', icon: <AutoAwesome fontSize="small" /> },
  { label: 'How much have I earned?', icon: <TrendingUp fontSize="small" /> },
  { label: 'Motivate me!', icon: <EmojiEvents fontSize="small" /> },
];

const WELCOME_MESSAGES = [
  "Hey! 👋 I'm Earn-E, your chore buddy. Ready to crush it today?",
  "What's up! I'm Earn-E — here to help you earn, save, and level up 🚀",
  "Hey! Earn-E here. Let's make today productive 💪 What's on your mind?",
];

function buildContextString(tasks, completedTasks, userProfile) {
  const pending = tasks ? tasks.filter(t => t.status === 'pending') : [];
  const earned = completedTasks ? completedTasks.reduce((s, t) => s + (t.allowance || 0), 0) : 0;
  const balance = userProfile ? (userProfile.allowance || 0) : 0;
  return `Child has ${pending.length} pending task(s). Total earned from completed tasks: $${earned.toFixed(2)}. Current allowance balance: $${balance.toFixed(2)}.`;
}

function EarnEChat({ tasks, completedTasks, userProfile, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const messagesEndRef = useRef(null);
  const isInitialMount = useRef(true);

  const safeUserProfile = useMemo(() => userProfile || {}, [userProfile]);
  const safeTasks = useMemo(() => tasks || [], [tasks]);
  const safeCompletedTasks = useMemo(() => completedTasks || [], [completedTasks]);

  const familyId = safeUserProfile.familyId || null;
  const childId = currentUser && currentUser.uid
    ? currentUser.uid.replace(/^child:/, '')
    : null;

  useEffect(() => {
    if (!initialized) {
      const welcome = WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
      setMessages([{ id: 1, type: 'earne', content: welcome, timestamp: new Date() }]);
      setInitialized(true);
    }
  }, [initialized]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = useCallback(async (messageText) => {
    const text = (messageText || input).trim();
    if (!text || !familyId || !childId) return;

    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    try {
      const contextNote = buildContextString(safeTasks, safeCompletedTasks, safeUserProfile);
      const fullMessage = text + '\n\n[App context: ' + contextNote + ']';
      const history = messages.slice(-6).map(m => ({ role: m.type === 'user' ? 'user' : 'assistant', content: m.content }));

      // Use client-side OpenAI call (like the old working version)
      const result = await sendToOpenAI({
        messages: [
          { role: 'system', content: `You are Earn-E, a friendly AI chore buddy for kids age ${safeUserProfile.age || 13}+. Keep responses encouraging, age-appropriate, and focused on chores/allowance/money skills.` },
          ...history,
          { role: 'user', content: fullMessage }
        ],
        maxTokens: 150
      });

      const aiMsg = { id: Date.now() + 1, type: 'earne', content: result.choices[0].message.content, timestamp: new Date() };
      setMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error('teenChat error:', error.code, error.message);
      let errorMsg = "🔧 Chat needs setup. Brian: Run these 3 commands:\n\n1. firebase login\n2. firebase functions:secrets:set OPENAI_API_KEY --project family-hub-9ae4e\n3. firebase deploy --only functions:teenChat --project family-hub-9ae4e\n\nSee DEPLOY-FIREBASE-FUNCTIONS.md for details.";
      
      if (error.code === 'functions/NOT_FOUND') {
        errorMsg = "⚠️ Chat function not deployed. Brian: Run 'firebase deploy --only functions:teenChat'";
      } else if (error.code === 'permission-denied') {
        errorMsg = "🔒 Permission denied. Please sign out and sign in again.";
      }
      
      const fallbackMsg = { id: Date.now() + 1, type: 'earne', content: errorMsg, timestamp: new Date() };
      setMessages(prev => [...prev, fallbackMsg]);
    } finally {
      setIsTyping(false);
    }
  }, [input, messages, familyId, childId, safeTasks, safeCompletedTasks, safeUserProfile]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  const handleQuickAction = useCallback((label) => {
    sendMessage(label);
  }, [sendMessage]);

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3, border: '2px solid #87CEEB', bgcolor: '#E3F2FD' }}>
      <CardContent sx={{ pb: 1 }}>
        <Box onClick={() => setExpanded(prev => !prev)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(135, 206, 235, 0.8)', border: '2px solid #87CEEB', width: 40, height: 40, fontSize: '1.2rem' }}> 🤖 </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="primary.main"> Earn-E </Typography>
              <Typography variant="caption" color="text.secondary"> Your AI Chore Buddy </Typography>
            </Box>
          </Box>
          <IconButton sx={{ minWidth: 48, minHeight: 48 }}> {expanded ? <ExpandLess /> : <ExpandMore />} </IconButton>
        </Box>
      </CardContent>

      <Collapse in={expanded}>
        <Divider />

        <Box sx={{ height: 280, overflow: 'auto', p: 2 }}>
          {messages.map((msg) => (
            <Box key={msg.id} sx={{ display: 'flex', flexDirection: msg.type === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-start', mb: 1.5, gap: 1 }} >
              <Avatar sx={{ bgcolor: msg.type === 'earne' ? 'rgba(135, 206, 235, 0.8)' : 'primary.main', width: 30, height: 30, fontSize: '0.85rem', flexShrink: 0 }}> {msg.type === 'earne' ? '🤖' : '👤'} </Avatar>
              <Paper sx={{ p: 1.5, maxWidth: '78%', borderRadius: 2, bgcolor: msg.type === 'earne' ? '#BBDEFB' : 'primary.light', border: msg.type === 'earne' ? '1px solid #87CEEB' : '1px solid transparent' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}> {msg.content} </Typography>
              </Paper>
            </Box>
          ))}

          {isTyping && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Avatar sx={{ bgcolor: 'rgba(135, 206, 235, 0.8)', width: 30, height: 30, fontSize: '0.85rem' }}> 🤖 </Avatar>
              <Paper sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(135, 206, 235, 0.12)' }}>
                <CircularProgress size={14} sx={{ color: 'primary.main' }} />
              </Paper>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {QUICK_ACTIONS.map((action, i) => (
            <Chip key={i} label={action.label} size="small" icon={action.icon} onClick={() => handleQuickAction(action.label)} disabled={isTyping} sx={{ cursor: 'pointer' }} />
          ))}
        </Box>

        <Divider />

        <CardContent sx={{ pt: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField fullWidth multiline maxRows={3} value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={handleKeyPress} placeholder="Ask Earn-E anything..." variant="outlined" size="small" disabled={isTyping} />
            <IconButton color="primary" onClick={() => sendMessage()} disabled={!input.trim() || isTyping} sx={{ minWidth: 48, minHeight: 48 }} > <Send /> </IconButton>
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
}

export default EarnEChat;
